import { ReactElement } from 'react'
import { FormattedMessage } from 'react-intl'
import { gql, useQuery, NetworkStatus } from '@apollo/client'
import styled from 'styled-components'
import { isEmpty } from 'lodash'

import { Button } from 'components/v2/Button'
import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Table, Thead, Th, Tbody, Tr, Td } from '@10xLabs/react-web/components/Table'
import { InputPercentage } from 'components/InputPercentage'
import { Switch } from 'components/v2/Switch'
import { ICabinSetting } from 'features/InventoryRules/components/CabinStrategiesForm/types'
import { ISelectBookingClassMaxLoadFactorProps } from 'features/InventoryRules/components/SelectBookingClassMaxLoadFactor/types'
import { FormikErrors } from 'formik'
import { GTBookingClass } from 'gql/types.generated'

const GET_BOOKING_CLASSES = gql`
  query GetBookingClasses($companyId: ID) {
    bookingClasses(input: { companyID: $companyId }) {
      id
      name
      priority
      isActive
      promo
    }
  }
`

const StyledTr = styled(Tr)`
  td,
  th {
    text-align: center;
  }

  td:first-child,
  th:first-child {
    text-align: left;
  }

  td:last-child,
  th:last-child {
    text-align: right;
  }
`

const StyledTd = styled(Td)`
  vertical-align: middle;
  font-size: 16px;
`

const StyledInputPercentage = styled(InputPercentage)`
  display: inline-block;

  > div {
    max-width: 90px;
    min-width: 50px;
    margin: 0 auto;
  }

  input {
    padding: 6px 9px;
    font-size: 16px;
    text-align: center;
  }
`

export const SelectBookingClassMaxLoadFactor = ({
  cabinSettings,
  defaultCabinSetting = { maxLoadFactor: '', isActive: true },
  setCabinSettings,
  onBlur,
  touched,
  errors,
  prefixId,
  companyId
}: ISelectBookingClassMaxLoadFactorProps): ReactElement => {
  const { loading, error, refetch, networkStatus } = useQuery<{ bookingClasses: GTBookingClass[] }>(
    GET_BOOKING_CLASSES,
    {
      fetchPolicy: 'cache-first',
      variables: {
        companyId
      },
      skip: !companyId,
      onCompleted(data) {
        const cabinSettingArray: GTBookingClass[] = []
        data.bookingClasses.forEach((bookingClass: TSFixMe): TSFixMe => {
          cabinSettingArray.push({ ...bookingClass, promo: false })
          if (bookingClass.promo) {
            cabinSettingArray.push({
              ...bookingClass,
              name: `${bookingClass.name}P`
            })
          }
        })
        setCabinSettings(
          cabinSettingArray
            .map((bookingClass): ICabinSetting => {
              const cabinSetting = cabinSettings.find(
                ({ bookingClassId, promo }) => bookingClassId === bookingClass.id && promo === bookingClass.promo
              )

              return {
                bookingClassId: bookingClass.id,
                name: bookingClass.name,
                priority: bookingClass.priority,
                maxLoadFactor: !isEmpty(cabinSetting) ? cabinSetting.maxLoadFactor : defaultCabinSetting.maxLoadFactor,
                isActive: !isEmpty(cabinSetting) ? cabinSetting.isActive : defaultCabinSetting.isActive,
                promo: bookingClass.promo
              }
            })
            .sort((a, b) => b.priority - a.priority)
        )
      }
    }
  )

  if (!companyId) {
    return (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="inventoryRules.cabinSetting.query.noCompany"
            defaultMessage="You need to select first a company"
          />
        </EmptyState.Description>
      </EmptyState.Container>
    )
  } else if (loading || networkStatus === NetworkStatus.refetch) {
    return (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    return (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="inventoryRules.cabinSetting.query.error.description"
            defaultMessage="There was an error while loading the booking class"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button onClick={(): TSFixMe => refetch()} kind="primary">
            <FormattedMessage
              id="inventoryRules.cabinSetting.query.error.action"
              defaultMessage="Reload your booking class"
            />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  }

  return (
    <Table>
      <Thead>
        <StyledTr>
          <Th>
            <FormattedMessage id="inventoryRules.cabinSetting.header.class" defaultMessage="Class" />
          </Th>
          <Th>
            <FormattedMessage id="inventoryRules.cabinSetting.header.maxLoadFactor" defaultMessage="Max. Load Factor" />
          </Th>
          <Th>
            <FormattedMessage id="inventoryRules.cabinSetting.header.openClose" defaultMessage="Open / Close" />
          </Th>
        </StyledTr>
      </Thead>
      <Tbody>
        {cabinSettings.map((cabinSetting: ICabinSetting, index: number): ReactElement => {
          const toggleActive = (): void => {
            setCabinSettings(
              cabinSettings.map((cabinSetting, cabinSettingIndex) => {
                if (cabinSettingIndex === index) {
                  return {
                    ...cabinSetting,
                    isActive: !cabinSetting.isActive
                  }
                }
                return cabinSetting
              })
            )
          }
          const isFirstCabinSetting = index === 0

          return (
            <StyledTr key={index}>
              <StyledTd>{cabinSetting.name}</StyledTd>
              <StyledTd>
                <StyledInputPercentage
                  id={`${prefixId}[${index}].maxLoadFactor`}
                  value={cabinSetting.maxLoadFactor}
                  placeholder="0%"
                  onChange={(value: TSFixMe): TSFixMe => {
                    setCabinSettings(
                      cabinSettings.map((cabinSetting, cabinSettingIndex) => {
                        if (cabinSettingIndex === index) {
                          return {
                            ...cabinSetting,
                            maxLoadFactor: value
                          }
                        }
                        if (!isFirstCabinSetting && cabinSettingIndex === index - 1 && parseFloat(value) === 100) {
                          return {
                            ...cabinSetting,
                            isActive: false
                          }
                        }
                        return cabinSetting
                      })
                    )
                  }}
                  onBlur={onBlur}
                  touched={touched?.[index]?.maxLoadFactor}
                  errors={(errors?.[index] as FormikErrors<ICabinSetting>)?.maxLoadFactor}
                  small
                />
              </StyledTd>
              <StyledTd>
                <Switch value={cabinSetting.isActive} onChange={toggleActive} small />
              </StyledTd>
            </StyledTr>
          )
        })}
      </Tbody>
    </Table>
  )
}
